import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, RefreshCw, Search, AlertTriangle, Pencil, ArrowUpDown, Trash2, Upload, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";
import PurgeDataButton from "@/components/PurgeDataButton";

const UNITS = ["tablet", "strip", "bottle", "ml", "pack", "sachet", "capsule"];

export default function Inventory() {
  const { branch, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [adjustItem, setAdjustItem] = useState(null);

  const canDelete = user?.role === "manager" || user?.role === "superadmin";
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, ok, fail, skipped, fileName }

  const downloadTemplate = () => {
    const csv = "sku,name,unit,stock_qty,mrp,selling_price,reorder_level\nASH-CAP,Ashwagandha Capsule,tablet,50,180,170,10\nDASH-100,Dashamoolarishtam 450ml,bottle,20,250,230,5\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "inventory_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    return lines.slice(1).map(line => {
      // simple quoted-field aware split
      const cells = []; let cur = ""; let inQ = false;
      for (const ch of line) {
        if (ch === '"') inQ = !inQ;
        else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
        else cur += ch;
      }
      cells.push(cur);
      const row = {};
      headers.forEach((h, i) => row[h] = (cells[i] || "").trim());
      return row;
    });
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    let ok = 0, fail = 0, skipped = 0;
    const failures = []; // { row, sku, name, reason }
    const noteFailure = (rowNum, sku, name, reason) => {
      if (failures.length < 10) failures.push({ row: rowNum, sku, name, reason });
    };
    try {
      let rows = [];
      const ext = file.name.toLowerCase().split(".").pop();
      if (ext === "xlsx" || ext === "xls") {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf);
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      } else {
        const text = await file.text();
        rows = parseCsv(text);
      }
      if (!rows.length) { toast.error("Empty sheet"); return; }

      setProgress({ current: 0, total: rows.length, ok: 0, fail: 0, skipped: 0, failures: [], fileName: file.name });

      // Flexible column mapping: sl.no/#, name/medicine name, rate/price, mrp, stock/qty, unit, sku
      const norm = (k) => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const pick = (row, ...keys) => {
        for (const key of keys) {
          for (const rk of Object.keys(row)) {
            if (norm(rk) === norm(key)) return row[rk];
          }
        }
        return "";
      };
      const slug = (s) => String(s).toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);

      let idx = 0;
      for (const r of rows) {
        idx++;
        const name = String(pick(r, "medicine name", "name", "item", "product", "description")).trim();
        if (!name) {
          skipped++;
          noteFailure(idx, "—", "(blank)", "Missing medicine name column");
          setProgress(p => p && ({ ...p, current: idx, skipped, failures: [...failures] }));
          continue;
        }
        const rateRaw = pick(r, "rate", "price", "selling price", "mrp");
        const rate = Number(String(rateRaw).replace(/[^0-9.]/g, "")) || 0;
        const mrp = Number(String(pick(r, "mrp") || rateRaw).replace(/[^0-9.]/g, "")) || rate;
        let sku = String(pick(r, "sku", "code")).trim();
        if (!sku) sku = slug(name);
        if (!sku) {
          skipped++;
          noteFailure(idx, "—", name, "Could not derive SKU from name");
          setProgress(p => p && ({ ...p, current: idx, skipped, failures: [...failures] }));
          continue;
        }
        try {
          await api.post("/inventory", {
            branch_code: branch.code, sku, name,
            unit: String(pick(r, "unit") || "tablet").trim(),
            stock_qty: Number(String(pick(r, "stock", "qty", "stock qty", "quantity")).replace(/[^0-9.]/g, "")) || 0,
            mrp, selling_price: rate,
            reorder_level: Number(String(pick(r, "reorder", "reorder level", "min")).replace(/[^0-9.]/g, "")) || 10,
          });
          ok++;
        } catch (err) {
          fail++;
          const reason = formatApiError(err, "Server rejected the row");
          noteFailure(idx, sku, name, reason);
        }
        setProgress(p => p && ({ ...p, current: idx, ok, fail, skipped, failures: [...failures] }));
      }
      toast.success(`Imported ${ok} · Failed ${fail} · Skipped ${skipped}`);
      load();
    } catch (err) {
      toast.error("Import failed — check the file format");
    } finally {
      setImporting(false);
      setProgress(p => p ? { ...p, done: true } : null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory", { params: { branch_code: branch.code, q: q || undefined, low_only: lowOnly || undefined } });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
    finally { setLoading(false); }
  }, [branch?.code, q, lowOnly]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/inventory/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err, "Failed")); }
  };

  const lowCount = rows.filter(r => r.low_stock).length;

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Inventory · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Medicine stock</h1>
          <p className="text-[#5C5652]">Medicines here feed the pharmacy billing screen — rates and stock stay in sync automatically.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="h-10 px-4 rounded-lg border border-sand bg-white text-sm font-semibold inline-flex items-center gap-2" data-testid="inv-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
          <button onClick={downloadTemplate} className="h-10 px-4 rounded-lg border border-sand bg-white text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2" data-testid="inv-template">
            <Download className="h-4 w-4" /> CSV template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="h-10 px-4 rounded-lg border border-sand bg-white text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2 disabled:opacity-60" data-testid="inv-import">
            <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import Excel/CSV"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="inv-file-input" />
          <PurgeDataButton collection="inventory" label="inventory" onDone={load} />
          <button onClick={() => setAddOpen(true)} className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2" data-testid="inv-add"><Plus className="h-4 w-4" /> Add item</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="p-4 rounded-lg bg-white border border-sand">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1">Items</div>
          <div className="font-display text-2xl text-forest">{rows.length}</div>
        </div>
        <div className="p-4 rounded-lg bg-white border border-sand">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1">Low stock</div>
          <div className={`font-display text-2xl ${lowCount ? "text-[#C25946]" : "text-forest"}`}>{lowCount}</div>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" />
          <input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or SKU…"
            className="w-full h-10 pl-10 pr-3 rounded-lg bg-white border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
        </div>
        <label className="text-sm inline-flex items-center gap-2">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} className="h-4 w-4 accent-[#1A4331]" data-testid="inv-low-only" />
          Only low stock
        </label>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
          : rows.length === 0 ? <div className="p-10 text-center text-sm text-[#5C5652]">No items{q && ` matching "${q}"`}.</div>
          : (
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="bg-[#F9F6F0] border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652]">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Item</th>
                  <th className="text-left px-4 py-3 font-bold">SKU</th>
                  <th className="text-right px-4 py-3 font-bold">Stock</th>
                  <th className="text-right px-4 py-3 font-bold">Rate</th>
                  <th className="text-right px-4 py-3 font-bold">MRP</th>
                  <th className="text-right px-4 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E3D9]">
                {rows.map(r => (
                  <tr key={r.id} data-testid={`inv-row-${r.sku}`} className="hover:bg-[#F9F6F0]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#2A2624]">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">per {r.unit}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#5C5652]">{r.sku}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-display text-lg ${r.low_stock ? "text-[#C25946]" : "text-forest"}`}>{r.stock_qty}</span>
                      {r.low_stock && <div className="text-[9px] uppercase tracking-wider text-[#C25946] inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> low</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">₹ {r.selling_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#5C5652]">₹ {r.mrp.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setAdjustItem(r)} data-testid={`inv-adjust-${r.sku}`} title="Adjust stock" className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><ArrowUpDown className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setEditItem(r)} data-testid={`inv-edit-${r.sku}`} title="Edit" className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
                        {canDelete && <button onClick={() => del(r.id)} data-testid={`inv-delete-${r.sku}`} title="Delete" className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5 text-[#C25946]" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {addOpen && <ItemModal branch={branch} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
      {editItem && <ItemModal branch={branch} item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
      {adjustItem && <AdjustModal item={adjustItem} onClose={() => setAdjustItem(null)} onSaved={() => { setAdjustItem(null); load(); }} />}
      {progress && (
        <ImportProgress
          progress={progress}
          importing={importing}
          onClose={() => setProgress(null)}
        />
      )}
    </div>
  );
}

function ImportProgress({ progress, importing, onClose }) {
  const { current = 0, total = 0, ok = 0, fail = 0, skipped = 0, failures = [], fileName = "" } = progress;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  const done = !importing;
  const shownFailures = failures.slice(0, 5);
  const moreCount = failures.length > 5 ? failures.length - 5 : 0;
  const totalRejected = skipped + fail;
  return (
    <Portal>
      <div
        className="fixed inset-0 bg-[#2A2624]/55 z-50 flex items-center justify-center p-6"
        onClick={done ? onClose : undefined}
        data-testid="inv-import-progress"
      >
        <div
          className="bg-white w-full max-w-md rounded-lg border border-sand p-6 animate-fade-up max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1 ornament-dot">
            {done ? "Import complete" : "Uploading stock sheet"}
          </div>
          <div className="font-display text-2xl text-forest leading-tight mb-1" data-testid="inv-import-progress-title">
            {done ? "Done" : `${current} / ${total} imported`}
          </div>
          <div className="text-xs text-[#5C5652] truncate mb-4" title={fileName}>
            {fileName}
          </div>

          <div className="h-3 rounded-full bg-[#F0EBDE] overflow-hidden border border-sand">
            <div
              data-testid="inv-import-progress-bar"
              className={`h-full transition-all duration-200 ${done ? "bg-forest" : "bg-[#E5A93D]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 text-right text-[11px] font-mono text-[#5C5652]" data-testid="inv-import-progress-pct">
            {pct}%
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            <div className="p-3 rounded-lg bg-[#1A4331]/[0.06] border border-sand" data-testid="inv-import-ok">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Added</div>
              <div className="font-display text-xl text-forest">{ok}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#E5A93D]/10 border border-sand" data-testid="inv-import-skipped">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Skipped</div>
              <div className="font-display text-xl text-[#8A5F1B]">{skipped}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#C25946]/10 border border-sand" data-testid="inv-import-fail">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Failed</div>
              <div className="font-display text-xl text-[#C25946]">{fail}</div>
            </div>
          </div>

          {shownFailures.length > 0 && (
            <div className="mt-5" data-testid="inv-import-failures">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652] mb-2 ornament-dot">
                Why {totalRejected} row{totalRejected === 1 ? "" : "s"} didn&apos;t import
              </div>
              <ul className="space-y-1.5 text-xs">
                {shownFailures.map((f, i) => (
                  <li
                    key={`${f.row}-${i}`}
                    data-testid={`inv-import-failure-${i}`}
                    className="p-2 rounded border border-[#C25946]/25 bg-[#C25946]/[0.06]"
                  >
                    <div className="font-mono text-[10px] text-[#5C5652]">
                      Row {f.row} · {f.sku}
                    </div>
                    <div className="text-[#2A2624] font-semibold truncate" title={f.name}>
                      {f.name}
                    </div>
                    <div className="text-[#C25946] mt-0.5">{f.reason}</div>
                  </li>
                ))}
              </ul>
              {moreCount > 0 && (
                <div className="text-[11px] text-[#5C5652] mt-2 text-center" data-testid="inv-import-failures-more">
                  + {moreCount} more not shown — fix these first and re-upload
                </div>
              )}
            </div>
          )}

          {done ? (
            <button
              onClick={onClose}
              data-testid="inv-import-progress-close"
              className="mt-5 w-full h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover"
            >
              Close
            </button>
          ) : (
            <div className="mt-5 text-center text-[11px] text-[#5C5652]">
              Keep this tab open — importing row by row so we don&apos;t overload the server.
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

function ItemModal({ branch, item, onClose, onSaved }) {
  const editing = !!item;
  const [form, setForm] = useState({
    sku: item?.sku || "", name: item?.name || "", unit: item?.unit || "tablet",
    stock_qty: item?.stock_qty ?? "", mrp: item?.mrp ?? "", selling_price: item?.selling_price ?? "",
    reorder_level: item?.reorder_level ?? 10,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU and name are required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/inventory/${item.id}`, {
          name: form.name, unit: form.unit,
          stock_qty: Number(form.stock_qty), mrp: Number(form.mrp),
          selling_price: Number(form.selling_price), reorder_level: Number(form.reorder_level),
        });
      } else {
        await api.post("/inventory", {
          branch_code: branch.code, sku: form.sku.trim(), name: form.name.trim(),
          unit: form.unit, stock_qty: Number(form.stock_qty) || 0,
          mrp: Number(form.mrp) || 0, selling_price: Number(form.selling_price) || 0,
          reorder_level: Number(form.reorder_level) || 10,
        });
      }
      toast.success(editing ? "Updated" : "Added to inventory");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="inv-item-modal">
        <div className="font-display text-2xl text-forest mb-4">{editing ? "Edit item" : `Add to ${branch.name}`}</div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="inv-sku" placeholder="SKU / code" value={form.sku} disabled={editing}
            onChange={(e) => setForm(f => ({...f, sku: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none col-span-2 disabled:bg-[#F9F6F0]" />
          <input data-testid="inv-name" placeholder="Name" value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none col-span-2" />
          <select data-testid="inv-unit" value={form.unit} onChange={(e) => setForm(f => ({...f, unit: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand bg-white focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none">
            {UNITS.map(u => <option key={u}>{u}</option>)}
          </select>
          <input data-testid="inv-stock" type="number" min="0" placeholder="Stock qty" value={form.stock_qty} onChange={(e) => setForm(f => ({...f, stock_qty: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="inv-mrp" type="number" min="0" step="0.01" placeholder="MRP ₹" value={form.mrp} onChange={(e) => setForm(f => ({...f, mrp: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="inv-price" type="number" min="0" step="0.01" placeholder="Selling price ₹" value={form.selling_price} onChange={(e) => setForm(f => ({...f, selling_price: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="inv-reorder" type="number" min="0" placeholder="Reorder at" value={form.reorder_level} onChange={(e) => setForm(f => ({...f, reorder_level: e.target.value}))}
            className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none col-span-2" />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="inv-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">{editing ? "Update" : "Add item"}</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function AdjustModal({ item, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("Restock");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = Number(delta);
    if (!n) { toast.error("Enter a positive or negative number"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/${item.id}/adjust`, { delta: n, reason });
      toast.success(`Stock ${n > 0 ? "increased" : "decreased"} by ${Math.abs(n)}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="inv-adjust-modal">
        <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1">{item.sku} · current {item.stock_qty}</div>
        <div className="font-display text-2xl text-forest mb-4">Adjust stock — {item.name}</div>
        <div className="space-y-3">
          <input data-testid="inv-delta" type="number" placeholder="Delta (e.g., +50 or -3)" value={delta} onChange={(e) => setDelta(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="inv-reason" placeholder="Reason (restock, breakage, expiry…)" value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="inv-adjust-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Apply</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
